import React, { useState, useEffect } from 'react';
import ReactFlow, { ReactFlowProvider, Controls, Background, useNodesState, useEdgesState } from 'reactflow';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import jalaali from 'jalaali-js';
import 'reactflow/dist/style.css';
import './BacklinkMapView.css';
import BacklinkSourceNode from './BacklinkSourceNode';
import TargetPageNode from './TargetPageNode';

const nodeTypes = {
  backlinkSource: BacklinkSourceNode,
  targetPage: TargetPageNode,
};

const ExportButton = () => {
  const [loading, setLoading] = useState(false);

  const onClick = () => {
    setLoading(true);
    const mapContainer = document.querySelector('#backlink-map-viewport');
    if (!mapContainer) {
      alert("خطا: عنصر نقشه یافت نشد.");
      setLoading(false);
      return;
    }

    html2canvas(mapContainer, {
      useCORS: true,
      scale: 1.5, // <-- کاهش مقیاس برای کاهش حجم اولیه
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/jpeg', 0.8); // <-- استفاده از فرمت JPEG با کیفیت 80%
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      // --- افزودن فشرده‌سازی JPEG به PDF ---
      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height, undefined, 'FAST'); // <-- استفاده از فشرده‌سازی سریع
      pdf.save('backlink-map.pdf');
      setLoading(false);
    }).catch(err => {
      console.error("خطا در ایجاد PDF:", err);
      alert("متاسفانه در ایجاد فایل PDF خطایی رخ داد.");
      setLoading(false);
    });
  };

  return <button onClick={onClick} className="export-btn" disabled={loading}>{loading ? 'در حال ساخت...' : 'خروجی PDF'}</button>;
};

const generateLayout = (linksData) => {
    const nodes = [];
    const edges = [];
    const nodeMap = new Map();
    const today = jalaali.toJalaali(new Date());
    const todayNumeric = today.jy * 10000 + today.jm * 100 + today.jd;

    const pages = {};
    linksData.forEach(link => {
        if (!pages[link.page_id]) pages[link.page_id] = { id: link.page_id, label: link.page_label, links: [] };
        pages[link.page_id].links.push(link);
    });

    let yOffset = 0;
    const columnX = { sites: 100, pages: 700 };
    
    Object.values(pages).forEach((page) => {
        const pageNodeId = `page-${page.id}`;
        const totalHeight = page.links.reduce((sum) => sum + 150, 0);
        const pageY = yOffset + totalHeight / 2 - 50;
        
        if (!nodeMap.has(pageNodeId)) {
            nodes.push({ id: pageNodeId, type: 'targetPage', position: { x: columnX.pages, y: pageY }, data: { label: page.label, linkCount: page.links.length } });
            nodeMap.set(pageNodeId, true);
        }

        page.links.forEach((link, index) => {
            const sourceNodeId = `site-${link.id}`;
            const linkType = (link.type || '').trim().toLowerCase();
            const isBacklink = linkType === 'backlink';
            
            let labelData = {};
            let status = '';
            let statusClassName = '';

            if (isBacklink) {
                if (link.end_date) {
                    const [endYear, endMonth, endDay] = link.end_date.split('/').map(Number);
                    if (todayNumeric > (endYear * 10000 + endMonth * 100 + endDay)) {
                        status = 'منقضی شده';
                        statusClassName = 'status-expired';
                    } else {
                        status = 'فعال';
                        statusClassName = 'status-active';
                    }
                } else {
                    status = 'فعال';
                    statusClassName = 'status-active';
                }
                
                labelData = { 
                    type: 'backlink', 
                    site_name: link.site_name, 
                    target_keyword: link.target_keyword, 
                    price1: Number(link.cost) || 0, 
                    start_date: link.start_date, 
                    end_date: link.end_date, 
                    status, 
                    statusClassName 
                };

            } else {
                const totalCost = Number(link.total_cost) || 0;
                const keywordCount = Number(link.keyword_count) || 1;
                labelData = { type: 'reportage', site_name: link.site_name, target_keyword: link.target_keyword, price1: totalCost, price2: Math.round(totalCost / keywordCount) };
            }

            nodes.push({ id: sourceNodeId, type: 'backlinkSource', position: { x: columnX.sites, y: yOffset }, data: labelData });
            
            edges.push({ id: `e-${link.id}`, source: sourceNodeId, target: pageNodeId, targetHandle: `handle-${index}`, type: 'smoothstep', label: isBacklink ? 'بک لینک' : 'رپورتاژ آگهی' });
            
            yOffset += 190;
        });
        
        yOffset += 140;
    });

    return { initialNodes: nodes, initialEdges: edges };
};

const BacklinkMapView = ({ pageIds, onClose }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const apiUrl = `https://sseo.afaq.lc/seo_architect_api/get_link_data.php?page_ids=${pageIds.join(',')}`;
    const token = localStorage.getItem('authToken');
    fetch(apiUrl, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          const { initialNodes, initialEdges } = generateLayout(data.data);
          setNodes(initialNodes);
          setEdges(initialEdges);
        } else {
          setError(data.message || 'خطا در دریافت اطلاعات لینک‌ها.');
        }
      })
      .catch(() => setError('خطای شبکه.'))
      .finally(() => setLoading(false));
  }, [pageIds, setNodes, setEdges]);

  return (
    <div className="map-view-overlay">
      <ReactFlowProvider>
        <div className="map-view-header">
          <h3>نقشه بک‌لینک‌ها</h3>
          <div className="header-actions">
            <ExportButton />
            <button onClick={onClose} className="close-btn">&times; بازگشت به نقشه اصلی</button>
          </div>
        </div>
        <div className="map-view-content">
          {loading && <div className="loading-text">در حال ساخت نقشه...</div>}
          {error && <div className="error-text">{error}</div>}
          {!loading && !error && (
            <div id="backlink-map-viewport" style={{ width: '100%', height: '100%' }}>
              <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} nodeTypes={nodeTypes} fitView>
                <Controls />
                <Background />
              </ReactFlow>
            </div>
          )}
        </div>
      </ReactFlowProvider>
    </div>
  );
};

export default BacklinkMapView;